import { useMemo } from "react";
import { FileText, Clock, Tag, Pencil } from "lucide-react";

/**
 * ProjectDescription — renders the user's free-text project briefing as a
 * professional, structured document with auto-detected headings, sub-headings,
 * bullets and numbered lists. Uses the full available width (no max-w clamp).
 *
 * Heuristics (applied per block separated by blank lines):
 *  - Lines starting with `# `  → H2
 *  - Lines starting with `## ` → H3
 *  - Lines starting with `### `→ H4
 *  - Lines that consist of `Title:` (≤ 80 chars, ends with ':') → H3
 *  - Multiple lines starting with `- `, `* `, `• ` or `–`  → unordered list
 *  - Multiple lines starting with `1.`, `2.` …            → ordered list
 *  - Otherwise → paragraph(s)
 *
 * Inline:
 *  - Bold: **text** → <strong>
 *  - URLs auto-linked
 */

const URL_RE = /(https?:\/\/[^\s)]+)/g;

function renderInline(text) {
  // Split on bold markers first
  const parts = [];
  let rest = text;
  let key = 0;
  while (rest.length) {
    const m = rest.match(/\*\*(.+?)\*\*/);
    if (!m) {
      parts.push(<span key={key++}>{linkify(rest, key)}</span>);
      break;
    }
    if (m.index > 0) parts.push(<span key={key++}>{linkify(rest.slice(0, m.index), key)}</span>);
    parts.push(<strong key={key++} className="text-white font-semibold">{linkify(m[1], key)}</strong>);
    rest = rest.slice(m.index + m[0].length);
  }
  return parts;
}

function linkify(text, baseKey) {
  if (!URL_RE.test(text)) return text;
  URL_RE.lastIndex = 0;
  const out = [];
  let last = 0;
  let k = 0;
  text.replace(URL_RE, (url, _g1, idx) => {
    if (idx > last) out.push(text.slice(last, idx));
    out.push(
      <a
        key={`${baseKey}-l-${k++}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#00F0FF] underline decoration-dotted underline-offset-2 hover:text-white transition-colors break-all"
      >
        {url}
      </a>
    );
    last = idx + url.length;
    return url;
  });
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function parseBlocks(raw) {
  if (!raw) return [];
  // Normalize CRLF → LF and collapse 3+ blank lines into double
  let normalized = String(raw).replace(/\r\n/g, "\n").replace(/\n{3,}/g, "\n\n");
  // Pre-pass: ensure heading lines (#, ##, ###) are isolated as their own
  // block by inserting blank lines around them. This allows them to render
  // even when the user writes them inline with body text in one block.
  normalized = normalized
    .split("\n")
    .reduce((acc, line, idx, arr) => {
      const isHeading = /^(#{1,3})\s+\S/.test(line);
      if (isHeading) {
        if (acc.length && acc[acc.length - 1].trim() !== "") acc.push("");
        acc.push(line);
        if (idx < arr.length - 1 && arr[idx + 1].trim() !== "") acc.push("");
      } else {
        acc.push(line);
      }
      return acc;
    }, [])
    .join("\n");

  const rawBlocks = normalized.split(/\n\s*\n/);
  const blocks = [];
  for (const blk of rawBlocks) {
    const lines = blk.split("\n").map((l) => l.replace(/\s+$/, "")).filter((l) => l.length > 0);
    if (!lines.length) continue;

    // Markdown-style headings (single line)
    if (lines.length === 1) {
      const h2 = /^#\s+(.+)$/.exec(lines[0]);
      const h3 = /^##\s+(.+)$/.exec(lines[0]);
      const h4 = /^###\s+(.+)$/.exec(lines[0]);
      if (h2) { blocks.push({ type: "h2", text: h2[1] }); continue; }
      if (h3) { blocks.push({ type: "h3", text: h3[1] }); continue; }
      if (h4) { blocks.push({ type: "h3", text: h4[1] }); continue; }
      // "Heading:" pattern (short label-style line ending with colon)
      if (lines[0].length <= 80 && /[:：]$/.test(lines[0]) && !/[.!?]/.test(lines[0].slice(0, -1))) {
        blocks.push({ type: "h3", text: lines[0].replace(/[:：]$/, "") });
        continue;
      }
    }

    // Unordered list
    const allBullets = lines.every((l) => /^[\s]*[-*•–]\s+/.test(l));
    if (allBullets && lines.length >= 2) {
      blocks.push({
        type: "ul",
        items: lines.map((l) => l.replace(/^[\s]*[-*•–]\s+/, "")),
      });
      continue;
    }

    // Ordered list
    const allNum = lines.every((l) => /^\s*\d+[.)]\s+/.test(l));
    if (allNum && lines.length >= 2) {
      blocks.push({
        type: "ol",
        items: lines.map((l) => l.replace(/^\s*\d+[.)]\s+/, "")),
      });
      continue;
    }

    // Mixed block: try to split at heading-like lines and at list runs.
    // We walk through the lines, batching consecutive paragraph-style lines
    // and emitting headings/lists as separate sub-blocks.
    let buffer = [];
    const flushParagraph = () => {
      if (!buffer.length) return;
      blocks.push({ type: "p", text: buffer.join(" ") });
      buffer = [];
    };
    let i = 0;
    while (i < lines.length) {
      const ln = lines[i];
      const headingMatch = /^(#{1,3})\s+(.+)$/.exec(ln);
      const labelMatch = ln.length <= 80 && /[:：]$/.test(ln) && !/[.!?]/.test(ln.slice(0, -1));
      const isBullet = /^[\s]*[-*•–]\s+/.test(ln);
      const isNum = /^\s*\d+[.)]\s+/.test(ln);

      if (headingMatch) {
        flushParagraph();
        const lvl = headingMatch[1].length;
        blocks.push({ type: lvl === 1 ? "h2" : "h3", text: headingMatch[2] });
        i++;
        continue;
      }
      if (labelMatch && (i + 1 < lines.length)) {
        flushParagraph();
        blocks.push({ type: "h3", text: ln.replace(/[:：]$/, "") });
        i++;
        continue;
      }
      if (isBullet) {
        flushParagraph();
        const items = [];
        while (i < lines.length && /^[\s]*[-*•–]\s+/.test(lines[i])) {
          items.push(lines[i].replace(/^[\s]*[-*•–]\s+/, ""));
          i++;
        }
        blocks.push({ type: "ul", items });
        continue;
      }
      if (isNum) {
        flushParagraph();
        const items = [];
        while (i < lines.length && /^\s*\d+[.)]\s+/.test(lines[i])) {
          items.push(lines[i].replace(/^\s*\d+[.)]\s+/, ""));
          i++;
        }
        blocks.push({ type: "ol", items });
        continue;
      }
      buffer.push(ln);
      i++;
    }
    flushParagraph();
  }
  return blocks;
}

function readingMinutes(text) {
  const words = (text || "").trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 220));
}

export default function ProjectDescription({
  description,
  category,
  title,
  index = 1,
  onEdit,
  showEditButton = false,
}) {
  const blocks = useMemo(() => parseBlocks(description), [description]);
  const wordCount = (description || "").trim().split(/\s+/).filter(Boolean).length;
  const readMin = readingMinutes(description);

  if (!description?.trim()) {
    return (
      <section
        data-testid="project-description-section"
        className="glass-surface rounded-2xl p-6 sm:p-8 border border-white/8"
      >
        <div className="flex items-center gap-3 mb-3">
          <span className="w-9 h-9 rounded-lg bg-[#00F0FF]/15 border border-[#00F0FF]/40 flex items-center justify-center text-[#00F0FF] font-['JetBrains_Mono'] text-sm font-bold">
            {index}
          </span>
          <h2 className="font-['Outfit'] text-xl sm:text-2xl text-white font-light tracking-tight">
            Projektbeschreibung
          </h2>
        </div>
        <p className="text-[#909098] italic text-sm">Keine Beschreibung vorhanden.</p>
      </section>
    );
  }

  return (
    <section
      data-testid="project-description-section"
      className="glass-surface rounded-2xl p-6 sm:p-8 border border-white/8"
    >
      {/* Section header: numbered chip + title + meta + edit */}
      <header className="flex items-start justify-between gap-3 flex-wrap mb-5 pb-4 border-b border-white/5">
        <div className="flex items-start gap-3">
          <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-[#00F0FF]/15 border border-[#00F0FF]/40 flex items-center justify-center text-[#00F0FF] font-['JetBrains_Mono'] text-sm font-bold">
            {index}
          </span>
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <FileText className="w-3.5 h-3.5 text-[#00F0FF]" />
              <span className="text-[10px] tracking-[0.25em] uppercase text-[#00F0FF] font-['JetBrains_Mono']">
                Briefing
              </span>
            </div>
            <h2 className="font-['Outfit'] text-xl sm:text-2xl text-white font-light tracking-tight">
              Projektbeschreibung
            </h2>
            {title && (
              <p className="text-[12px] text-[#909098] font-['JetBrains_Mono'] mt-0.5 truncate max-w-[60ch]">
                {title}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {category && (
            <span className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase px-2 py-1 rounded-md bg-[#00F0FF]/10 text-[#00F0FF] font-['JetBrains_Mono']">
              <Tag className="w-3 h-3" /> {category}
            </span>
          )}
          <span className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase px-2 py-1 rounded-md bg-white/5 text-[#909098] font-['JetBrains_Mono']">
            <Clock className="w-3 h-3" /> {readMin} min · {wordCount} Wörter
          </span>
          {showEditButton && onEdit && (
            <button
              data-testid="description-edit-btn"
              onClick={onEdit}
              className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-white hover:bg-white/10 hover:border-[#00F0FF]/30 transition-all font-['Outfit']"
              title="Beschreibung bearbeiten"
            >
              <Pencil className="w-3 h-3" /> Bearbeiten
            </button>
          )}
        </div>
      </header>

      {/* Body — full width, two-column on desktop for long blocks */}
      <article className="prose-radar max-w-none text-[15px] leading-[1.7] text-[#C8C8D2] font-['Inter']">
        {blocks.length === 0 ? (
          <p className="whitespace-pre-line">{description}</p>
        ) : (
          blocks.map((b, i) => {
            switch (b.type) {
              case "h2":
                return (
                  <h3
                    key={i}
                    className="font-['Outfit'] text-xl sm:text-[22px] text-white font-medium tracking-tight mt-6 first:mt-0 mb-2"
                  >
                    {renderInline(b.text)}
                  </h3>
                );
              case "h3":
                return (
                  <h4
                    key={i}
                    className="font-['Outfit'] text-base sm:text-lg text-white font-semibold mt-5 first:mt-0 mb-2 flex items-center gap-2"
                  >
                    <span className="w-1 h-4 bg-[#00F0FF] rounded-sm" />
                    {renderInline(b.text)}
                  </h4>
                );
              case "h4":
                return (
                  <h5
                    key={i}
                    className="font-['Outfit'] text-sm uppercase tracking-widest text-[#00F0FF] mt-4 mb-2 font-semibold"
                  >
                    {renderInline(b.text)}
                  </h5>
                );
              case "ul":
                return (
                  <ul key={i} className="space-y-1.5 mb-4 ml-1">
                    {b.items.map((it, j) => (
                      <li key={j} className="flex gap-2.5">
                        <span className="text-[#00F0FF] mt-[7px] flex-shrink-0">▸</span>
                        <span>{renderInline(it)}</span>
                      </li>
                    ))}
                  </ul>
                );
              case "ol":
                return (
                  <ol key={i} className="space-y-1.5 mb-4 ml-1 counter-reset" style={{ counterReset: "ol-radar 0" }}>
                    {b.items.map((it, j) => (
                      <li key={j} className="flex gap-2.5" style={{ counterIncrement: "ol-radar" }}>
                        <span
                          className="text-[#00F0FF] font-['JetBrains_Mono'] text-[12px] mt-[3px] flex-shrink-0 min-w-[18px]"
                          style={{ content: "counter(ol-radar)" }}
                        >
                          {String(j + 1).padStart(2, "0")}
                        </span>
                        <span>{renderInline(it)}</span>
                      </li>
                    ))}
                  </ol>
                );
              case "p":
              default:
                return (
                  <p key={i} className="mb-3 last:mb-0">
                    {renderInline(b.text)}
                  </p>
                );
            }
          })
        )}
      </article>
    </section>
  );
}
